import { BoardType, TileType, DirectionType } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Constants
export const GRID_SIZE = 4;
export const WINNING_TILE = 2048;

// Create an empty board
export const createEmptyBoard = (): BoardType => {
  return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(null));
};

// Initialize board with two random tiles
export const initializeBoard = (): BoardType => {
  const board = createEmptyBoard();
  addRandomTile(board);
  addRandomTile(board);
  return board;
};

// Check if coordinates are within board bounds
const isWithinBounds = (x: number, y: number): boolean => {
  return x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE;
};

// Find all empty cells on the board
export const getEmptyCells = (board: BoardType): { x: number, y: number }[] => {
  const emptyCells: { x: number, y: number }[] = [];
  
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (!board[y][x]) {
        emptyCells.push({ x, y });
      }
    }
  }
  
  return emptyCells;
};

// Add a random tile (2 or 4) to an empty cell
export const addRandomTile = (board: BoardType): BoardType => {
  const emptyCells = getEmptyCells(board);
  
  if (emptyCells.length === 0) return board;
  
  const { x, y } = emptyCells[Math.floor(Math.random() * emptyCells.length)];
  const value = Math.random() < 0.9 ? 2 : 4;
  
  const newBoard = [...board];
  newBoard[y][x] = {
    value,
    id: uuidv4(),
    isNew: true,
    position: { x, y }
  };
  
  return newBoard;
};

// Clone the board
export const cloneBoard = (board: BoardType): BoardType => {
  return board.map(row => [...row]);
};

// Check if two boards are equal
export const boardsEqual = (board1: BoardType, board2: BoardType): boolean => {
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      const tile1 = board1[y][x];
      const tile2 = board2[y][x];
      
      if (!tile1 && !tile2) continue;
      if (!tile1 || !tile2) return false;
      if (tile1.value !== tile2.value) return false;
    }
  }
  
  return true;
};

// Check if the game is over (no moves possible)
export const isGameOver = (board: BoardType): boolean => {
  // Check for empty cells
  if (getEmptyCells(board).length > 0) return false;
  
  // Check for possible merges horizontally
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE - 1; x++) {
      if (board[y][x]?.value === board[y][x + 1]?.value) {
        return false;
      }
    }
  }
  
  // Check for possible merges vertically
  for (let y = 0; y < GRID_SIZE - 1; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (board[y][x]?.value === board[y + 1][x]?.value) {
        return false;
      }
    }
  }
  
  return true;
};

// Check if the player has won (has a tile with value of WINNING_TILE)
export const hasWon = (board: BoardType): boolean => {
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (board[y][x]?.value === WINNING_TILE) {
        return true;
      }
    }
  }
  
  return false;
};

// Get cell movement vectors based on direction
export const getDirectionVectors = (direction: DirectionType): { dx: number, dy: number } => {
  switch (direction) {
    case 'UP':
      return { dx: 0, dy: -1 };
    case 'RIGHT':
      return { dx: 1, dy: 0 };
    case 'DOWN':
      return { dx: 0, dy: 1 };
    case 'LEFT':
      return { dx: -1, dy: 0 };
    default:
      return { dx: 0, dy: 0 };
  }
};

// Get the traversal order based on direction
export const getTraversalOrder = (direction: DirectionType): { x: number[], y: number[] } => {
  const x = Array.from({ length: GRID_SIZE }, (_, i) => i);
  const y = Array.from({ length: GRID_SIZE }, (_, i) => i);
  
  if (direction === 'RIGHT') x.reverse();
  if (direction === 'DOWN') y.reverse();
  
  return { x, y };
};

// Find the farthest position a tile can move in a given direction
export const findFarthestPosition = (
  board: BoardType,
  x: number,
  y: number,
  dx: number,
  dy: number
): { farthest: { x: number, y: number }, next: { x: number, y: number } | null } => {
  let prevX = x;
  let prevY = y;
  
  do {
    prevX = x;
    prevY = y;
    x += dx;
    y += dy;
  } while (isWithinBounds(x, y) && !board[y][x]);
  
  return {
    farthest: { x: prevX, y: prevY },
    next: isWithinBounds(x, y) ? { x, y } : null
  };
};